/**
 * POSTLAUNCH-V4 · v4-Issue #2 (Do 03.09.2026, 22:35) — Dashboard moderat aufgebaut.
 *
 * Steve-Jobs-Test bleibt: Vieles verdichtet, aber sauber gestaffelt in drei Zonen.
 *
 * OBEN  — Begrüßung + Willkommens-Video-Platzhalter (Andreas' Vimeo folgt)
 * MITTE — Fokustyp-Karte + Empfehlungs-CTA (M1 unverändert)
 * UNTEN — Verdichtungs-Sektion mit Status + Tipps + Fortschritts-Accordion
 *
 * Pricing-konform: nur der 39-€-Anker im Free-Fortschritt.
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight, Sparkles, ChevronDown, ChevronUp, CheckCircle2, Compass, PlayCircle,
  Calendar, MessageSquare, Lightbulb, HelpCircle, Flame, BookOpen,
} from "lucide-react";
import RedesignLayout from "@/components/redesign/RedesignLayout";
import { FOKUSTYP_PROFILES } from "@/lib/fokustypProfiles";
import { isPremium } from "@/lib/licensing";
import { FokustypIcon } from "@/components/redesign/FokustypIcon";
import { getFokustypTestRoute } from "@/lib/testRouting";
import FlexpfadVerdichtung from "@/components/redesign/FlexpfadVerdichtung";
import { STECKBRIEF_ERKENNUNG } from "@/lib/steckbriefErkennung";
import { openGlossar } from "@/components/redesign/Glossar";
import BringAFriendBanner from "@/components/redesign/BringAFriendBanner";
import PersonenRail from "@/components/redesign/PersonenRail";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { trackDashboardView } from "@/lib/ga4";
import UpgradeOfferBanner from "@/components/redesign/UpgradeOfferBanner";
import VideoDripTile from "@/components/dashboard/VideoDripTile";
import ChallengeDashboardTile from "@/components/challenge/ChallengeDashboardTile";
import TrialCountdownBanner from "@/components/TrialCountdownBanner";

// Sprint-V24 Hotfix (11.09.2026, Andreas-Direktive „Video wieder inline") —
// Andreas-Thumbnail fuer den Video-Hero. Wird beim Klick durch den Vimeo-
// Iframe an derselben Stelle ersetzt (autoplay=1). Kein Modal-Overlay.
const WELCOME_VIDEO_THUMBNAIL =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_botschaften-formel/artifacts/yqkrnl00_Kopie%20von%20F4S_Startseite-Still.png";

const SLUG_TO_KEY = { hund: "dog", eule: "owl", katze: "cat", biene: "bee" };
const NAMES = { hund: "Hund", eule: "Eule", katze: "Katze", biene: "Biene" };

function daysSince(iso) {
  if (!iso) return null;
  try {
    const start = new Date(iso);
    const diff = Date.now() - start.getTime();
    return Math.max(0, Math.floor(diff / (1000 * 60 * 60 * 24)));
  } catch {
    return null;
  }
}

// -------------------------------------------------------------
// (Oben) Begrüßungs-Header — nur Text, kein Avatar (Avatar bleibt in TopBar)
// -------------------------------------------------------------
function WelcomeHeader({ user }) {
  const firstName = user?.first_name || "";
  const memberSince = user?.pionier_since || user?.powerpaket_granted_at || null;
  const memberSinceLabel = memberSince
    ? new Date(memberSince).toLocaleDateString("de-DE", { year: "numeric", month: "long" })
    : null;
  return (
    <section data-testid="dashboard-welcome-header">
      {/* Sprint-V24 Issue I (11.02.2026, Boardroom-CoS „Issue-I-Video-Modal") —
          personalisierte Begruessung „Hallo, {firstName}!". Warmer, direkter
          Einstieg statt generischem Willkommen-Eyebrow + langem Subheading. */}
      <h1
        className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight"
        data-testid="dashboard-welcome-name"
      >
        {firstName ? `Hallo, ${firstName}!` : "Hallo!"}
      </h1>
      {memberSinceLabel && (
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400" data-testid="dashboard-welcome-since">
          Mitglied seit {memberSinceLabel}
        </p>
      )}
    </section>
  );
}

// -------------------------------------------------------------
// (Oben) Willkommens-Video · Inline-Player
// Sprint-V24 Hotfix (11.09.2026, Andreas-Direktive): Zurück auf Inline-
// Embed. Thumbnail-Klick tauscht das Bild gegen den Vimeo-Iframe im
// gleichen aspect-video-Container (autoplay=1). Kein Modal-Overlay.
// -------------------------------------------------------------
// -------------------------------------------------------------
// WelcomeVideo — Sprint V28 (Fr 27.02.2026): einklappbar via localStorage.
// Neue User: default AUFGEKLAPPT (Onboarding-Wert). Wenn der User einmal
// eingeklappt hat, bleibt der Zustand ueber Refreshes und Logins gespeichert
// (Key `f4s_dashboard_welcome_video_collapsed=true`), damit Wiederkehrer
// nicht bei jedem Cockpit-Besuch das Video-Thumbnail sehen muessen.
// -------------------------------------------------------------
const WELCOME_VIDEO_COLLAPSE_KEY = "f4s_dashboard_welcome_video_collapsed";

function WelcomeVideo() {
  const [playing, setPlaying] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(WELCOME_VIDEO_COLLAPSE_KEY) === "true";
    } catch (_e) {
      return false;
    }
  });

  const toggleCollapsed = () => {
    setCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(WELCOME_VIDEO_COLLAPSE_KEY, next ? "true" : "false");
      } catch (_e) { /* silent */ }
      // Beim Einklappen laufendes Video pausieren (Iframe unmount).
      if (next) setPlaying(false);
      return next;
    });
  };

  const VIMEO_ID = process.env.REACT_APP_VIMEO_VIDEO_WELCOME || "1225000893";
  const VIMEO_HASH = process.env.REACT_APP_VIMEO_VIDEO_WELCOME_HASH || "bf40542280";
  const iframeSrc = `https://player.vimeo.com/video/${VIMEO_ID}?h=${VIMEO_HASH}&autoplay=1&title=0&byline=0&portrait=0&dnt=1`;

  // Eingeklappt: kompakter Header mit Auf-Toggle
  if (collapsed) {
    return (
      <button
        type="button"
        onClick={toggleCollapsed}
        data-testid="dashboard-welcome-video-collapsed"
        aria-expanded="false"
        aria-label="Video-Begrüßung von Andreas einblenden"
        className="w-full flex items-center justify-between rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 py-3 hover:border-[var(--fts-primary-bright)]/60 transition-colors"
      >
        <span className="flex items-center gap-3 min-w-0">
          <span className="flex-shrink-0 w-9 h-9 rounded-full bg-[var(--fts-primary-bright)]/10 flex items-center justify-center">
            <PlayCircle className="w-5 h-5 text-[var(--fts-primary-bright)]" strokeWidth={1.75} aria-hidden="true" />
          </span>
          <span className="min-w-0 text-left">
            <span className="block text-sm font-bold text-slate-900 dark:text-white truncate">
              Andreas' persönliche Begrüßung
            </span>
            <span className="block text-xs text-slate-500 dark:text-slate-400 truncate">
              2 Minuten · einblenden
            </span>
          </span>
        </span>
        <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" aria-hidden="true" />
      </button>
    );
  }

  return (
    <div className="relative" data-testid="dashboard-welcome-video-expanded">
      {/* Einklapp-Toggle rechts oben ueber dem Video */}
      <button
        type="button"
        onClick={toggleCollapsed}
        data-testid="dashboard-welcome-video-collapse-btn"
        aria-expanded="true"
        aria-label="Video-Begrüßung einklappen"
        className="absolute top-2 right-2 z-10 inline-flex items-center gap-1 rounded-full bg-white/85 hover:bg-white backdrop-blur-sm text-slate-700 text-[11px] font-semibold px-2.5 py-1 shadow-md transition-colors"
      >
        Einklappen <ChevronUp className="w-3.5 h-3.5" aria-hidden="true" />
      </button>
      {playing ? (
        <div
          className="relative w-full rounded-2xl overflow-hidden bg-slate-900 shadow-lg"
          data-testid="dashboard-welcome-video-inline"
        >
          <div className="aspect-video w-full">
            <iframe
              src={iframeSrc}
              title="Andreas' persönliche Begrüßung"
              data-testid="dashboard-welcome-video-iframe"
              allow="autoplay; fullscreen; picture-in-picture; clipboard-write"
              allowFullScreen
              className="w-full h-full"
              frameBorder="0"
            />
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          data-testid="dashboard-welcome-video-thumbnail"
          aria-label="Video-Begrüßung von Andreas abspielen"
          className="group relative w-full rounded-2xl overflow-hidden bg-slate-900 shadow-lg hover:shadow-xl transition-shadow focus:outline-none focus:ring-2 focus:ring-[var(--fts-primary-bright)] focus:ring-offset-2 dark:focus:ring-offset-slate-900"
        >
          <div className="aspect-video w-full">
            <img
              src={WELCOME_VIDEO_THUMBNAIL}
              alt="Andreas Begrüßung — Vorschaubild"
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-[1.02]"
            />
          </div>
          <div className="absolute bottom-3 right-3 pointer-events-none">
            <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-full bg-white/85 backdrop-blur-sm shadow-lg flex items-center justify-center group-hover:scale-110 group-hover:bg-white transition-all">
              <PlayCircle className="w-6 h-6 sm:w-7 sm:h-7 text-[var(--fts-primary-bright)]" strokeWidth={1.75} />
            </div>
          </div>
          <div className="absolute bottom-3 left-4 right-16 sm:right-20 flex items-end justify-between pointer-events-none">
            <div className="min-w-0">
              <p className="text-white text-sm sm:text-base font-extrabold drop-shadow-lg truncate">
                Andreas' persönliche Begrüßung
              </p>
              <p className="text-white/80 text-xs sm:text-sm drop-shadow-lg">
                2 Minuten · Zum Abspielen tippen
              </p>
            </div>
          </div>
        </button>
      )}
    </div>
  );
}

// -------------------------------------------------------------
// (Mitte) Fokustyp-Karte — „Wer bin ich?"
// -------------------------------------------------------------
function FokustypCard({ slug, secondarySlug, user }) {
  const navigate = useNavigate();

  if (!slug) {
    const testRoute = getFokustypTestRoute(user);
    const showProHint = !isPremium(user);
    return (
      <section
        className="rounded-2xl border-2 border-[var(--fts-primary-bright)] bg-white dark:bg-slate-900 p-6"
        data-testid="dashboard-fokustyp-empty"
      >
        <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          Dein Fokustyp
        </p>
        <h2 className="mt-1 text-2xl sm:text-3xl font-extrabold text-slate-900 dark:text-white tracking-tight">
          Noch kein Fokustyp gesetzt.
        </h2>
        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300 max-w-xl">
          Drei Minuten Test, dann zeigt dir dein PocketCoach, wie du tickst — und wie du dich für andere lesbar machst.
        </p>
        <button
          type="button"
          onClick={() => navigate(testRoute)}
          data-testid="dashboard-fokustyp-empty-cta"
          className="mt-4 inline-flex items-center gap-2 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-5 py-2.5"
        >
          Zeig mir meinen Fokustyp <ArrowRight className="w-4 h-4" />
        </button>
        {showProHint && (
          <p
            className="mt-3 text-xs text-slate-500 dark:text-slate-400 max-w-xl"
            data-testid="dashboard-fokustyp-empty-pro-hint"
          >
            Nach dem QuickCheck (5 Fragen): der große 20-Fragen-Test mit tieferem Sekundär-Anteil und Stressmuster wird in{" "}
            <button
              type="button"
              onClick={() => navigate("/pricing")}
              data-testid="dashboard-fokustyp-empty-pro-hint-link"
              className="font-bold text-[var(--fts-primary-bright)] hover:underline"
            >
              Pro
            </button>{" "}
            freigeschaltet.
          </p>
        )}
      </section>
    );
  }

  const profile = FOKUSTYP_PROFILES[SLUG_TO_KEY[slug]] || {};
  const merkmale = profile.erkennungsmerkmale || [];
  const secondaryName = secondarySlug ? NAMES[secondarySlug] : null;

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border-2 border-[var(--fts-primary-bright)]/60 shadow-md p-6"
      data-testid="dashboard-fokustyp-card"
      data-slug={slug}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Dein dominierender Fokus-Typ
          </p>
          <h2
            className="mt-0.5 text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight"
            data-testid="dashboard-fokustyp-name"
          >
            Typ {profile.name || NAMES[slug]}
          </h2>
          <p className="mt-1.5 text-sm sm:text-base text-slate-700 dark:text-slate-200">
            {profile.tagline}
          </p>
          {secondaryName && (
            <p className="mt-1 text-xs text-slate-600 dark:text-slate-300" data-testid="dashboard-fokustyp-secondary">
              Sekundärer Anteil: <b className="text-slate-900 dark:text-white">{secondaryName}</b>
            </p>
          )}
        </div>
        <span className="flex-shrink-0" aria-hidden="true">
          <FokustypIcon slug={slug} size={80} />
        </span>
      </div>

      {merkmale.length > 0 && (
        <div className="mt-4" data-testid="dashboard-fokustyp-charakteristika">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-2">
            So tickst du
          </p>
          <ul className="space-y-1.5 text-sm text-slate-700 dark:text-slate-200">
            {merkmale.slice(0, 3).map((m, i) => (
              <li key={i} className="flex items-start gap-2">
                <CheckCircle2 className="w-3.5 h-3.5 mt-0.5 flex-shrink-0 text-[var(--fts-primary-bright)]" aria-hidden="true" />
                <span className="leading-snug">{m}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <button
        type="button"
        onClick={() => navigate("/mein-muster")}
        data-testid="dashboard-fokustyp-open-spiegel"
        className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--fts-primary-bright)] hover:underline"
      >
        Zum vollen Spiegel <ArrowRight className="w-3 h-3" />
      </button>
    </section>
  );
}

// -------------------------------------------------------------
// (Mitte) Empfehlungs-CTA — wert-basiert (M7 POSTLAUNCH-V3)
// -------------------------------------------------------------
function EmpfehlungsCta({ hasFokustyp, user, recommendation }) {
  const navigate = useNavigate();

  // POSTLAUNCH-V25 (Bugfix Doppel-CTA): Wenn kein Fokustyp gesetzt ist, übernimmt
  // ausschließlich die FokustypCard den Test-CTA. Der Empfehlungs-CTA erscheint
  // erst nach gesetztem Fokustyp und zeigt dann die nächste Flexpfad-Empfehlung.
  if (!hasFokustyp) {
    return null;
  }

  const rec = recommendation || {
    title: "Baue deinen nächsten Flexpfad.",
    body: "Ein kurzes Anliegen (~3 Min) — daraus ziehst du deinen konkreten ersten Aufschlag und wirst beim nächsten Gespräch treffsicherer.",
    cta_label: "Anliegen starten",
    cta_to: "/anliegen",
  };

  return (
    <button
      type="button"
      onClick={() => navigate(rec.cta_to || "/anliegen")}
      data-testid="dashboard-empfehlung-cta"
      className="w-full text-left rounded-2xl bg-[var(--fts-primary-bright)] hover:opacity-95 text-white p-6 shadow-md"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-white/85 flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Übung starten
          </p>
          <p
            className="mt-1 text-xl sm:text-2xl font-extrabold leading-tight"
            data-testid="dashboard-empfehlung-title"
          >
            {rec.title}
          </p>
          <p
            className="mt-1 text-sm text-white/90 max-w-xl"
            data-testid="dashboard-empfehlung-body"
          >
            {rec.body}
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold">
            {rec.cta_label || "Weiter"} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </button>
  );
}

// -------------------------------------------------------------
// (Unten) Status & Dynamik — 3 Kompakt-Kacheln
// -------------------------------------------------------------
function StatusDynamik({ daysActive, anliegenUsed, anliegenLimit, isPro, weekCount }) {
  const tiles = [
    {
      key: "aktiv",
      icon: Calendar,
      value: daysActive !== null ? String(daysActive) : "–",
      label: daysActive === 1 ? "Tag aktiv" : "Tage aktiv",
    },
    {
      key: "anliegen-heute",
      icon: MessageSquare,
      value: isPro ? String(anliegenUsed) : `${anliegenUsed}/${anliegenLimit}`,
      label: isPro ? "Anliegen heute" : "Freie Anliegen heute",
    },
    {
      key: "flexpfade-woche",
      icon: Compass,
      value: String(weekCount ?? 0),
      label: (weekCount ?? 0) === 1 ? "Flexpfad diese Woche" : "Flexpfade diese Woche",
    },
  ];
  return (
    <section
      className="grid grid-cols-1 sm:grid-cols-3 gap-3"
      data-testid="dashboard-status-dynamik"
    >
      {tiles.map((t) => {
        const Icon = t.icon;
        return (
          <div
            key={t.key}
            className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4"
            data-testid={`dashboard-stat-${t.key}`}
          >
            <div className="flex items-center gap-2 text-slate-500 dark:text-slate-400">
              <Icon className="w-3.5 h-3.5" aria-hidden="true" />
              <span className="text-[10px] font-bold uppercase tracking-widest">
                {t.label}
              </span>
            </div>
            <p className="mt-1 text-2xl font-extrabold text-slate-900 dark:text-white tabular-nums">
              {t.value}
            </p>
          </div>
        );
      })}
    </section>
  );
}

// -------------------------------------------------------------
// (Unten) Personalisierter Tipp — je nach Fokustyp
// -------------------------------------------------------------
// Sprint V94 (Mo 15.09.2026, Andreas-Direktive) — Von hardcoded Single-Tip
// auf API-getriebene Wochen-Rotation umgestellt. Content-Bibliothek (5 Tipps
// pro Fokustyp) liegt in `backend/content/weekly_tips.py`. Admin kann via
// `POST /api/admin/tips/pin` einen bestimmten Tipp pinnen.
function TippKachel({ slug }) {
  const [tip, setTip] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!slug) { setTip(null); return; }
    let cancelled = false;
    setLoading(true);
    api
      .get(`/tips/weekly?fokustyp=${encodeURIComponent(slug)}`)
      .then((r) => { if (!cancelled) setTip(r.data); })
      .catch(() => { if (!cancelled) setTip(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  if (!slug) return null;
  if (loading && !tip) {
    return (
      <section
        className="rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 p-5"
        data-testid="dashboard-tipp-loading"
        aria-busy="true"
      >
        <div className="animate-pulse space-y-2">
          <div className="h-3 w-32 bg-amber-200/60 dark:bg-amber-800/40 rounded" />
          <div className="h-4 w-full bg-amber-200/60 dark:bg-amber-800/40 rounded" />
          <div className="h-4 w-3/4 bg-amber-200/60 dark:bg-amber-800/40 rounded" />
        </div>
      </section>
    );
  }
  if (!tip) return null;
  return (
    <section
      className="rounded-2xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/60 p-5"
      data-testid="dashboard-tipp"
      data-slug={slug}
      data-tip-id={tip.id}
    >
      <div className="flex items-start gap-3">
        <Lightbulb className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-700 dark:text-amber-300">
              Tipp für diese Woche
            </p>
            {tip.kategorie && (
              <span
                className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200"
                data-testid="dashboard-tipp-kategorie"
              >
                {tip.kategorie}
              </span>
            )}
          </div>
          {tip.titel && (
            <p
              className="mt-2 text-sm font-bold text-slate-900 dark:text-slate-100"
              data-testid="dashboard-tipp-titel"
            >
              {tip.titel}
            </p>
          )}
          <p className="mt-1 text-sm text-slate-800 dark:text-slate-100 leading-relaxed">
            {tip.body}
          </p>
        </div>
      </div>
    </section>
  );
}

// -------------------------------------------------------------
// (Unten) Fortschritts-Accordion — Details on demand
// -------------------------------------------------------------
function FortschrittAccordion({ summary, isPro, anliegenUsed, anliegenLimit }) {
  const [open, setOpen] = useState(false);
  const total = summary?.total ?? 0;
  const week = summary?.week_count ?? 0;
  const streak = summary?.streak_days ?? 0;

  let summaryText;
  if (total === 0) {
    summaryText = "Noch keine Flexpfade gebaut. Fang mit deinem ersten an.";
  } else {
    const parts = [];
    parts.push(`${total} ${total === 1 ? "Flexpfad" : "Flexpfade"} gebaut`);
    if (streak > 0) parts.push(`Serie: ${streak} ${streak === 1 ? "Tag" : "Tage"}`);
    if (week > 0) parts.push(`diese Woche ${week}`);
    summaryText = parts.join(" · ");
  }

  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800"
      data-testid="dashboard-fortschritt"
      data-open={open ? "true" : "false"}
    >
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        data-testid="dashboard-fortschritt-toggle"
        aria-expanded={open}
        className="w-full text-left flex items-center justify-between gap-3 p-5 hover:bg-slate-50 dark:hover:bg-slate-800/40 rounded-2xl"
      >
        <div className="min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)] flex items-center gap-1.5">
            <Compass className="w-3.5 h-3.5" aria-hidden="true" /> Dein Fortschritt (Details)
          </p>
          <p
            className="mt-1 text-sm text-slate-800 dark:text-slate-200 leading-snug"
            data-testid="dashboard-fortschritt-summary"
          >
            {summaryText}
          </p>
          {!isPro && (
            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
              Heute {anliegenUsed}/{anliegenLimit} freie Anliegen genutzt · Pro Monat startet bei 39 €/Monat.
            </p>
          )}
        </div>
        <ChevronDown
          className={["w-5 h-5 text-slate-400 dark:text-slate-500 flex-shrink-0 transition-transform", open ? "rotate-180" : ""].join(" ")}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div className="px-5 pb-5 pt-1 border-t border-slate-100 dark:border-slate-800" data-testid="dashboard-fortschritt-details">
          <FlexpfadVerdichtung />
        </div>
      )}
    </section>
  );
}

// -------------------------------------------------------------
// (RECHTE SPALTE) Right-Rail — Brücken-Frage + Glossar-Quick + Streak
// POSTLAUNCH-V4+ (Fr 04.09.2026, CoS-Feedback rechte Spalte ungenutzt)
// -------------------------------------------------------------
function BrueckenFrageRail({ slug }) {
  const navigate = useNavigate();
  const erk = slug ? STECKBRIEF_ERKENNUNG[slug] : null;
  const frage = erk?.bruecken_frage;
  const clickable = !!slug;
  const Wrap = clickable ? "button" : "div";
  const wrapProps = clickable
    ? {
        type: "button",
        onClick: () => navigate(`/dashboard/fokustyp/${slug}`),
        "data-testid": "dashboard-right-bruecken-frage",
        "data-clickable": "true",
        className:
          "w-full text-left rounded-2xl bg-[var(--fts-primary-bright)]/10 hover:bg-[var(--fts-primary-bright)]/20 border-l-4 border-[var(--fts-primary-bright)] p-5 transition-colors group",
        "aria-label": `Steckbrief ${slug} öffnen`,
      }
    : {
        "data-testid": "dashboard-right-bruecken-frage",
        "data-clickable": "false",
        className:
          "rounded-2xl bg-[var(--fts-primary-bright)]/10 border-l-4 border-[var(--fts-primary-bright)] p-5",
      };
  return (
    <Wrap {...wrapProps}>
      <div className="flex items-start gap-2.5">
        <HelpCircle className="w-5 h-5 text-[var(--fts-primary-bright)] mt-0.5 flex-shrink-0" aria-hidden="true" />
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Wen erkennst du wieder?
          </p>
          <p className="mt-2 text-sm text-slate-800 dark:text-slate-100 leading-snug">
            {frage || "Nach deinem Fokustyp-Test siehst du hier eine persönliche Wiedererkennungs-Frage — der emotionale Anker zu deinem Muster."}
          </p>
          {clickable && (
            <p className="mt-2 text-[11px] font-bold text-[var(--fts-primary-bright)] group-hover:underline inline-flex items-center gap-1">
              Zum vollen Steckbrief <ArrowRight className="w-3 h-3" />
            </p>
          )}
        </div>
      </div>
    </Wrap>
  );
}

const KERN_QUICK = [
  { term: "Anliegen", hint: "Deine Situation" },
  { term: "Gegenüber", hint: "Wer vor dir steht" },
  { term: "Türöffner", hint: "Dein erster Satz" },
];

function GlossarQuickRail() {
  return (
    <section
      className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-5"
      data-testid="dashboard-right-glossar"
    >
      <div className="flex items-center gap-2 mb-3">
        <BookOpen className="w-4 h-4 text-[var(--fts-primary-bright)]" aria-hidden="true" />
        <p className="text-[10px] font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
          Drei Kernbegriffe
        </p>
      </div>
      <ul className="space-y-1.5">
        {KERN_QUICK.map((k) => (
          <li key={k.term}>
            <button
              type="button"
              onClick={() => openGlossar(k.term)}
              data-testid={`dashboard-right-glossar-${k.term.toLowerCase()}`}
              className="w-full text-left rounded-lg px-2.5 py-2 hover:bg-slate-50 dark:hover:bg-slate-800/60 transition-colors group"
            >
              <p className="text-sm font-bold text-slate-900 dark:text-white group-hover:text-[var(--fts-primary-bright)]">
                {k.term}
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400 leading-snug">
                {k.hint}
              </p>
            </button>
          </li>
        ))}
      </ul>
      <button
        type="button"
        onClick={() => openGlossar(null)}
        data-testid="dashboard-right-glossar-more"
        className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[var(--fts-primary-bright)] hover:underline"
      >
        Volles Glossar öffnen <ArrowRight className="w-3 h-3" />
      </button>
    </section>
  );
}

function StreakRail({ streak, week }) {
  const days = streak || 0;
  return (
    <section
      className={[
        "rounded-2xl border p-5",
        days > 0
          ? "bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-800/60"
          : "bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800",
      ].join(" ")}
      data-testid="dashboard-right-streak"
    >
      <div className="flex items-center gap-2 mb-2">
        <Flame className={["w-4 h-4", days > 0 ? "text-orange-500 dark:text-orange-400" : "text-slate-400 dark:text-slate-500"].join(" ")} aria-hidden="true" />
        <p className={["text-[10px] font-bold uppercase tracking-widest", days > 0 ? "text-orange-600 dark:text-orange-300" : "text-slate-500 dark:text-slate-400"].join(" ")}>
          Wochen-Serie
        </p>
      </div>
      <p
        className="text-3xl font-extrabold text-slate-900 dark:text-white tabular-nums"
        data-testid="dashboard-right-streak-days"
      >
        {days}
      </p>
      <p className="text-sm text-slate-600 dark:text-slate-300">
        {days === 1 ? "Tag in Folge" : "Tage in Folge"}
      </p>
      {(week ?? 0) > 0 && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
          Diese Woche: <b className="text-slate-700 dark:text-slate-200">{week}</b> {week === 1 ? "Flexpfad" : "Flexpfade"}
        </p>
      )}
      {days === 0 && (
        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400 leading-snug">
          Starte heute — auch 1 Flexpfad zählt.
        </p>
      )}
    </section>
  );
}

function RightRail({ slug, summary }) {
  return (
    <aside
      className="space-y-4"
      data-testid="dashboard-right-rail"
      aria-label="Ergänzende Informationen"
    >
      {/* Neue Reihenfolge (POSTLAUNCH-V6 Patch #1, Fr 04.09.2026 vor Launch 19:00):
          1. Wochen-Serie · 2. Wen hast du im Kopf · 3. Bring a Friend · 4. Drei Kernbegriffe
          BrueckenFrageRail entfernt — Personen-Rail ersetzt den emotionalen Anker interaktiv. */}
      <StreakRail streak={summary?.streak_days} week={summary?.week_count} />
      <PersonenRail />
      <BringAFriendBanner />
      <GlossarQuickRail />
    </aside>
  );
}

// -------------------------------------------------------------
// Main Page
// -------------------------------------------------------------
export default function RedesignDashboard() {
  const [summary, setSummary] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/dashboard/flexpfad-summary")
      .then((res) => { if (alive) setSummary(res.data); })
      .catch(() => { /* silent */ });
    return () => { alive = false; };
  }, []);

  return (
    <RedesignLayout>
      {({ data, reload }) => {
        const user = data?.user || {};
        const rawSlug = user.primary_fokustyp || user.fokustyp_primary || null;
        const slug = rawSlug && ["eule", "hund", "katze", "biene"].includes(String(rawSlug).toLowerCase())
          ? String(rawSlug).toLowerCase()
          : null;
        const secondarySlug = user.secondary_fokustyp && slug !== user.secondary_fokustyp
          ? String(user.secondary_fokustyp).toLowerCase()
          : null;
        const pro = isPremium(user);
        const anliegenUsed = user.anliegen_used_today ?? 0;
        const anliegenLimit = user.anliegen_limit_today ?? (pro ? 999 : 3);
        const daysActive = daysSince(user.pionier_since || user.powerpaket_granted_at);

        // Sprint V27 GA4 — Custom `dashboard_view`-Event (1x pro Session).
        // Idempotenz uebernimmt trackDashboardView selbst.
        if (data?.user) {
          trackDashboardView({ tier: pro ? "pro" : "free", hasFokustyp: !!slug });
        }

        return (
          <div
            className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
            data-testid="dashboard-verdichtung"
            data-tier={pro ? "pro" : "free"}
            data-has-fokustyp={slug ? "true" : "false"}
          >
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 lg:gap-6">
              {/* LINKE SPALTE (2/3 auf lg+) — Sprint V28 A6 (Fr 27.02.2026):
                  Reorganisation nach Andreas-Direktive „Fortschritt/Fokustyp/
                  Flexpfad nach oben". Kern-Werte (was bin ich? was mache
                  ich als naechstes? wo stehe ich?) stehen jetzt oben.
                  Willkommens-Video bleibt oben, ist aber einklappbar (Status
                  persistiert in localStorage, damit Wiederkehrer freie Sicht
                  auf ihre Kern-Werte haben). */}
              <div className="lg:col-span-2 space-y-5" data-testid="dashboard-main-col">
                {/* 1) Begruessung — kurz, persoenlich */}
                <WelcomeHeader user={user} />

                {/* Sprint V103.ch (Fr 02.10.2026) — 5-Tage-Challenge-Kachel.
                    Rendert NULL wenn kein Challenge-Member (Negativtest).
                    Prominent oben, damit Teilnehmer sofort ihren naechsten Tag sehen. */}
                <ChallengeDashboardTile />

                {/* Sprint V101 (27.02.2026) — Video-Drip-In fuer Free-User.
                    Prominent oben, damit neue User sofort das heute
                    freigeschaltete Video sehen. Rendert null wenn nicht
                    aktiv (Pro-User, Drip-Fenster abgelaufen, opt-out). */}
                <VideoDripTile user={user} />

                {/* Sprint V102.i (23.09.2026) — Trial-Countdown fuer Soft-Trial-User.
                    Sprint V102.j.1 (27.09.2026) — Cancel-Handler eingehaengt,
                    damit User den Trial selbst beenden koennen (Copy-Versprechen).
                    Rendert null wenn `license_tier != trial` oder `trial_ends_at` fehlt. */}
                <TrialCountdownBanner
                  user={user}
                  onCancelTrial={async () => {
                    // eslint-disable-next-line no-alert
                    const ok = window.confirm(
                      "Testzeitraum wirklich beenden? Du gehst sofort zurück in den Free-Plan.",
                    );
                    if (!ok) return;
                    try {
                      await api.post("/user/trial/cancel");
                      if (typeof reload === "function") reload();
                      toast.success("Testzeitraum beendet — du bist zurück im Free-Plan.");
                    } catch (e) {
                      toast.error("Beenden fehlgeschlagen — bitte erneut versuchen.");
                    }
                  }}
                />

                {/* Retention-Upgrade-Fenster (Sprint V30): dezenter Banner
                    fuer Pro-Solo-Monatsabonnenten, 28 Tage ab first_paid_at.
                    Rendert sich selbst zu null wenn nicht eligible. */}
                <UpgradeOfferBanner user={user} />

                {/* 2) Willkommens-Video — einklappbar, Onboarding-Wert bleibt sichtbar */}
                <WelcomeVideo />

                {/* 3) Fokustyp prominent — der wichtigste Wert-Anker */}
                <FokustypCard slug={slug} secondarySlug={secondarySlug} user={user} />

                {/* 4) Flexpfad-Empfehlung — naechster konkreter Schritt fuer den User */}
                <EmpfehlungsCta
                  hasFokustyp={!!slug}
                  user={user}
                  recommendation={summary?.recommendation}
                />

                {/* 5) Fortschritt — sichtbar machen, was schon erreicht wurde */}
                <FortschrittAccordion
                  summary={summary}
                  isPro={pro}
                  anliegenUsed={anliegenUsed}
                  anliegenLimit={anliegenLimit}
                />

                {/* 6) Status-Dynamik — Anliegen-Zaehler + Wochenpuls */}
                <StatusDynamik
                  daysActive={daysActive}
                  anliegenUsed={anliegenUsed}
                  anliegenLimit={anliegenLimit}
                  isPro={pro}
                  weekCount={summary?.week_count}
                />

                {/* 7) Tipp — Micro-Tipp zum Fokustyp */}
                {slug && <TippKachel slug={slug} />}
              </div>

              {/* RECHTE SPALTE — Right-Rail (Brücken-Frage · Glossar · Streak) */}
              <RightRail slug={slug} summary={summary} />
            </div>
          </div>
        );
      }}
    </RedesignLayout>
  );
}
